/* eslint-disable eslint-plugin/prefer-message-ids, eslint-plugin/prefer-object-rule, eslint-plugin/require-meta-type, eslint-plugin/require-meta-schema */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Config from '../../../../../Config';
import type ProblemDomainSelectionBlockData from '../../../../../Models/Content/ProblemDomainSelectionBlockData';
import { Onboarding } from '../../../../Page/SelfCarePage/SelfCareAsyncThunk';
import { hideAppLoader, showAppLoader } from '../../../../Shared/Loader/LoaderSlice';
import { iconBackChevron, iconDomainRowChevron } from '../../images';
import {
  getSubscriptionsForProductWorld,
  isSubscribersByProductLoaded,
  linesForWorld,
  resolveDomainRowFromCms,
  tabKeyFromProductWorld,
  PRODUCT_SERVICE_WORLD,
} from '../../problemDomainHelpers';
import { getInternetProducts, getSubscribersByProductType } from '../../problemDomainAsyncThunk';
import '../DomainSelectionPage/domainSelectionPage.scss';
import './subscriptionSelectionPage.scss';

type CmsImageRef = {
  expandedValue?: { url?: string };
  value?: { url?: string };
};

type ProblemDomainItem = {
  domainTitle?: { value?: string };
  domainSelectionIcon?: CmsImageRef;
  productWorld?: { value?: unknown } | unknown;
  contentLink?: { id?: number | string };
  subscriptionPickerIcon?: CmsImageRef;
  title?: { value?: string };
  subtitle?: { value?: string };
};

const TV_PRODUCT_TYPE = 'TV';

const subscriptionKey = (sub: Record<string, unknown>, fallback: number | string = ''): string =>
  String(sub.productSubscriberNo ?? sub.subscriberNo ?? sub.ban ?? fallback);

const mediaUrl = (imageProp: CmsImageRef | undefined): string | null => {
  if (!imageProp) {
    return null;
  }
  const path = imageProp.expandedValue?.url || imageProp.value?.url;
  return path ? Config.epiBaseUrl + path : null;
};

export type SubscriptionOption = {
  title: string;
  raw: Record<string, unknown>;
};

type OwnProps = {
  iContent: ProblemDomainSelectionBlockData;
  productWorld: number;
  domainContentLinkId?: number | string | null;
  onBack: () => void;
  onSelectSubscription: (option: SubscriptionOption, options?: { skippedSelection?: boolean }) => void;
};

const SubscriptionSelectionPageView = (props: OwnProps): React.ReactElement => {
  const { iContent, productWorld, domainContentLinkId, onBack, onSelectSubscription } = props;
  const dispatch = useDispatch();
  const isInternetWorld = productWorld === PRODUCT_SERVICE_WORLD.Internet;
  const isTvWorld = productWorld === PRODUCT_SERVICE_WORLD.TV;
  const isEnergyWorld = productWorld === PRODUCT_SERVICE_WORLD.Energy;
  const [internetProducts, setInternetProducts] = useState([]);
  const [isInternetProductsReady, setIsInternetProductsReady] = useState(!isInternetWorld);
  const [tvSubscribers, setTvSubscribers] = useState([]);
  const [isTvSubscribersReady, setIsTvSubscribersReady] = useState(!isTvWorld);
  const autoSelectedSubscriptionKey = useRef<string | null>(null);

  useEffect(() => {
    if (!isInternetWorld) {
      setInternetProducts([]);
      setIsInternetProductsReady(true);
      return;
    }

    let cancelled = false;
    setIsInternetProductsReady(false);
    dispatch(showAppLoader(undefined));

    const loadInternetProducts = async () => {
      try {
        const products = await getInternetProducts();
        if (!cancelled) {
          setInternetProducts(products);
        }
      } finally {
        if (!cancelled) {
          setIsInternetProductsReady(true);
          dispatch(hideAppLoader(undefined));
        }
      }
    };

    loadInternetProducts();

    return () => {
      cancelled = true;
      dispatch(hideAppLoader(undefined));
    };
  }, [dispatch, isInternetWorld]);

  useEffect(() => {
    if (!isTvWorld) {
      setTvSubscribers([]);
      setIsTvSubscribersReady(true);
      return;
    }

    let cancelled = false;
    setIsTvSubscribersReady(false);
    dispatch(showAppLoader(undefined));

    const loadTvSubscribers = async () => {
      try {
        const subscribers = await getSubscribersByProductType(TV_PRODUCT_TYPE);
        if (!cancelled) {
          setTvSubscribers(subscribers);
        }
      } finally {
        if (!cancelled) {
          setIsTvSubscribersReady(true);
          dispatch(hideAppLoader(undefined));
        }
      }
    };

    loadTvSubscribers();

    return () => {
      cancelled = true;
      dispatch(hideAppLoader(undefined));
    };
  }, [dispatch, isTvWorld]);

  const subscribersByProduct = useSelector(
    (state: {
      selfcarePage?: { subscribersByProduct?: Record<string, Record<string, unknown>[]> };
    }) => state.selfcarePage?.subscribersByProduct,
  );
  const isFetchLoginData = useSelector((state: { selfcarePage?: { isFetchLoginData?: boolean } }) =>
    Boolean(state.selfcarePage?.isFetchLoginData),
  );

  const tabKey = tabKeyFromProductWorld(productWorld);
  const productsLoaded = isSubscribersByProductLoaded(subscribersByProduct);

  useEffect(() => {
    if (!isFetchLoginData || !tabKey) {
      return;
    }
    if (productsLoaded) {
      return;
    }
    dispatch(Onboarding());
  }, [dispatch, isFetchLoginData, subscribersByProduct, tabKey, productsLoaded]);

  const domainRow = useMemo(() => {
    const domainItems = (iContent?.domainItems?.expandedValue || []) as unknown as ProblemDomainItem[];
    return resolveDomainRowFromCms(domainItems, productWorld, domainContentLinkId);
  }, [iContent?.domainItems?.expandedValue, productWorld, domainContentLinkId]);

  const heading = domainRow?.title?.value;
  const subtitle = domainRow?.subtitle?.value;
  const iconUrl = mediaUrl(domainRow?.subscriptionPickerIcon);

  const bgDesktop = mediaUrl(iContent?.backgroundImageDesktop as unknown as CmsImageRef);
  const bgMobile = mediaUrl(iContent?.backgroundImageMobile as unknown as CmsImageRef);

  const sectionStyle = {
    ...(bgDesktop ? { '--prob-domain-bg-desktop': `url("${bgDesktop}")` } : {}),
    ...(bgMobile ? { '--prob-domain-bg-mobile': `url("${bgMobile}")` } : {}),
  } as React.CSSProperties;

  const subscriptions = getSubscriptionsForProductWorld(subscribersByProduct, productWorld);

  const linesForSubscription = useCallback((sub: Record<string, unknown>) => {
    if (isTvWorld) {
      const address = sub.address as { fullAddress?: string; FullAddress?: string } | undefined;
      const fullAddress = String(address?.fullAddress ?? address?.FullAddress ?? '').trim();
      const description = String(sub.productDescription ?? sub.ProductDescription ?? '').trim();
      if (fullAddress || description) {
        return { primary: fullAddress, secondary: description || undefined };
      }
    }

    return linesForWorld(productWorld, sub);
  }, [isTvWorld, productWorld]);

  const handlePick = useCallback((sub: Record<string, unknown>, options?: { skippedSelection?: boolean }) => {
    const { primary, secondary } = linesForSubscription(sub);
    const title = secondary ? `${primary} ${secondary}` : primary;
    onSelectSubscription({ title: title.trim(), raw: sub }, options);
  }, [linesForSubscription, onSelectSubscription]);

  const itemsToRender =
    isInternetWorld && internetProducts.length > 0
      ? internetProducts
      : isTvWorld && tvSubscribers.length > 0
        ? tvSubscribers
        : subscriptions;

  const isListReady =
    (!isInternetWorld || isInternetProductsReady) && (!isTvWorld || isTvSubscribersReady);
  const shouldSkipSubscriptionSelection =
    isListReady && (itemsToRender.length === 1 || (isEnergyWorld && itemsToRender.length > 0));

  useEffect(() => {
    if (!shouldSkipSubscriptionSelection) {
      return;
    }

    const onlySubscription = itemsToRender[0] as Record<string, unknown>;
    const key = subscriptionKey(onlySubscription, productWorld);
    if (autoSelectedSubscriptionKey.current === key) {
      return;
    }

    autoSelectedSubscriptionKey.current = key;
    handlePick(onlySubscription, { skippedSelection: true });
  }, [handlePick, itemsToRender, productWorld, shouldSkipSubscriptionSelection]);

  const shouldHideSubscriptionSelection = !isListReady || shouldSkipSubscriptionSelection;

  if (shouldHideSubscriptionSelection) {
    return <section className="problem-domain-selection problem-domain-subscription" style={sectionStyle} />;
  }

  return (
    <section className="problem-domain-selection problem-domain-subscription" style={sectionStyle}>
      <header className="problem-domain-selection__top">
        <button type="button" className="problem-domain-selection__back" onClick={onBack}>
          <span className="problem-domain-selection__back-content">
            <img
              src={iconBackChevron}
              alt=""
              width={16}
              height={16}
              className="problem-domain-selection__back-icon"
              aria-hidden
            />
            <span>חזרה</span>
          </span>
        </button>
      </header>

      <div className="problem-domain-subscription__shell">
        <div className="problem-domain-subscription__hero">
          {iconUrl ? (
            <div className="problem-domain-subscription__icon-wrap">
              <img src={iconUrl} alt="" className="problem-domain-subscription__icon" />
            </div>
          ) : null}
          {heading ? <h1 className="problem-domain-subscription__heading">{heading}</h1> : null}
          {subtitle ? <p className="problem-domain-subscription__subtitle">{subtitle}</p> : null}
        </div>

        {isListReady && !shouldSkipSubscriptionSelection ? (
        <ul className="problem-domain-subscription__list" role="list">
            {itemsToRender.map((sub, idx) => {
              const { primary, secondary } = linesForSubscription(sub);
              const ban = sub?.ban;
              const internetProduct = internetProducts.find(
                (product) => product?.ban === ban
              );
              const address = internetProduct?.address || primary;
              const packageDescription = internetProduct?.description || secondary;

              const key = subscriptionKey(sub, idx);
              const singleLine = !secondary;

              return (
                <li key={key} className="problem-domain-subscription__item">
                  <button
                    type="button"
                    className="problem-domain-subscription__card"
                    onClick={() => handlePick(sub)}
                    aria-label={secondary ? `${primary}, ${secondary}` : primary}
                  >
                    <span className="problem-domain-subscription__card-text">
                      <span
                        className={
                          singleLine
                            ? 'problem-domain-subscription__line problem-domain-subscription__line--primary-only'
                            : 'problem-domain-subscription__line problem-domain-subscription__line--strong'
                        }
                      >
                        {address ||primary }
                      </span>
                      {packageDescription || secondary ? (
                        <span className="problem-domain-subscription__line problem-domain-subscription__line--muted">
                          {packageDescription || secondary}
                        </span>
                      ) : null}
                    </span>
                    <span className="problem-domain-selection__chevron-btn" aria-hidden>
                      <img
                        src={iconDomainRowChevron}
                        width={9}
                        height={14}
                        className="problem-domain-selection__chevron-img"
                        alt=""
                      />
                    </span>
                  </button>
                </li>
              );
            })}
        </ul>
        ) : null}
      </div>
    </section>
  );
};

export default SubscriptionSelectionPageView;
